import OliverText from "@builder/components/general/OliverText";
import "./style.scss";
import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useOutsideClick } from "@builder/hooks/useOutsideClick";

type CheckboxOption = {
    checked: boolean;
};

interface CheckboxDropdownProps<T extends CheckboxOption> {
    label?: string;
    placeholder?: string;
    options: T[];
    callback: (option: T) => void;
    getLabel: (option: T) => string;
    searchable?: boolean;
    replaceSelectedLabel?: string;
}

const CheckboxDropdown = <T extends CheckboxOption>({
    label,
    placeholder,
    options,
    callback,
    getLabel,
    replaceSelectedLabel,
    searchable
}: CheckboxDropdownProps<T>) => {
    const [open, setOpen] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");
    const [filteredOptions, setFilteredOptions] = useState<T[]>(options);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (searchable) {
            setSearch(e.target.value);
        }
    };

    useEffect(() => {
        const trimSearch = search.trim();
        if (trimSearch) {
            setFilteredOptions(
                options.reduce((acc: T[], option: T) => {
                    const normalizedOptionLabel = getLabel(option).toLowerCase().trim();
                    const normalizedSearch = trimSearch.toLowerCase();
                    if (normalizedOptionLabel.includes(normalizedSearch)) {
                        acc.push(option);
                    }
                    return acc;
                }, [])
            );
        } else {
            setFilteredOptions(options);
        }
    }, [search, options]);

    const selectRef = useRef<HTMLDivElement>(null);
    useOutsideClick(selectRef, () => {
        setOpen(false);
    });

    const getInputValue = () => {
        if (replaceSelectedLabel && options.some(option => option.checked)) {
            return replaceSelectedLabel;
        }
        let inputValue = "";
        options.forEach(option => {
            if (option.checked) {
                if (inputValue) {
                    inputValue += `, ${getLabel(option)}`;
                } else {
                    inputValue += getLabel(option);
                }
            }
        });
        return inputValue;
    };

    const handleOptionClick = (option: T) => {
        callback(option);
    };

    const wrapperClass = classNames("checkbox-dropdown-wrapper", {
        ["input-open"]: open
    });

    return (
        <div className={wrapperClass} ref={selectRef}>
            {label && <OliverText text={label} size="xs" />}
            <img src="./angled-arrow-down.svg" className="arrow-down" onClick={() => setOpen(!open)} />
            <input
                type="text"
                placeholder={placeholder}
                readOnly={!searchable}
                value={searchable ? search : getInputValue()}
                onClick={() => setOpen(!open)}
                onInput={handleInput}
            />
            {open && (
                <div className="options">
                    {filteredOptions.map((option, index) => (
                        <div className="option" key={index} onClick={() => handleOptionClick(option)}>
                            <div className="checkbox">{option.checked && <img src="./blue-checkmark.svg" />}</div>
                            <p>{getLabel(option)}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CheckboxDropdown;
